import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Input,
  Select,
  Row,
  Col,
  Button,
  DatePicker,
  Modal,
  Descriptions,
} from "antd";
import {
  SearchOutlined,
  CarOutlined,
  ReloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { getParkingTickets } from "../../api/parkpass/parkpassParkingTickets";
import {
  getUserNames,
} from "../../api/parkpass/parkpassWalletTransactions";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const statusColors = {
  pending: "orange",
  active: "green",
  expired: "red",
  cancelled: "default",
};

const statusLabels = {
  pending: "En attente",
  active: "Actif",
  expired: "Expiré",
  cancelled: "Annulé",
};

const paymentMethodColors = {
  wallet: "blue",
  cash: "green",
  card: "purple",
  fatourati: "magenta",
  cmi: "gold",
  online: "cyan",
};

const paymentMethodLabels = {
  wallet: "Portefeuille",
  cash: "Espèces",
  card: "Carte",
  fatourati: "Fatourati",
  cmi: "CMI",
  online: "En ligne",
};

const CitizenParkingTicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    statusFilter: undefined,
    matricule: undefined,
    startTimeFrom: undefined,
    startTimeEnd: undefined,
    userId: undefined,
  });

  useEffect(() => {
    fetchTickets();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await getUserNames();
      setUsers(response || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchTickets = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    currentFilters = filters
  ) => {
    try {
      setLoading(true);
      const response = await getParkingTickets({
        page,
        pageSize,
        statusFilter: currentFilters.statusFilter,
        matricule: currentFilters.matricule,
        startTimeFrom: currentFilters.startTimeFrom,
        startTimeEnd: currentFilters.startTimeEnd,
        userId: currentFilters.userId,
      });
      setTickets(response.items);
      setPagination({
        current: response.page,
        pageSize: response.page_size,
        total: response.total,
      });
    } catch (error) {
      console.error("Error fetching parking tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchTickets(newPagination.current, newPagination.pageSize, filters);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setFilters({
        ...filters,
        startTimeFrom: dates[0].toISOString(),
        startTimeEnd: dates[1].toISOString(),
      });
    } else {
      setFilters({
        ...filters,
        startTimeFrom: undefined,
        startTimeEnd: undefined,
      });
    }
  };

  const handleSearch = () => {
    fetchTickets(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    const resetFilters = {
      statusFilter: undefined,
      matricule: undefined,
      startTimeFrom: undefined,
      startTimeEnd: undefined,
      userId: undefined,
    };
    setFilters(resetFilters);
    fetchTickets(1, pagination.pageSize, resetFilters);
  };

  const handleViewDetail = (record) => {
    setSelectedTicket(record);
    setDetailModalVisible(true);
  };

  const handleCloseDetail = () => {
    setDetailModalVisible(false);
    setSelectedTicket(null);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "UUID",
      dataIndex: "uuid",
      key: "uuid",
      width: 180,
      render: (uuid) => (
        <span style={{ fontFamily: "monospace", fontSize: 11 }}>
          {uuid?.substring(0, 8)}...
        </span>
      ),
    },
    {
      title: "Véhicule",
      dataIndex: "vehicle_plate",
      key: "vehicle_plate",
      width: 120,
      render: (plate) => (
        <Tag color="blue" icon={<CarOutlined />}>
          {plate}
        </Tag>
      ),
    },
    {
      title: "Parking",
      key: "parking",
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.parking_name}</div>
          <div style={{ fontSize: 11, color: "#999" }}>
            Ligne: {record.line_name}
          </div>
        </div>
      ),
    },
    {
      title: "Durée",
      dataIndex: "duration_minutes",
      key: "duration_minutes",
      width: 100,
      render: (minutes) => (
        <span style={{ fontWeight: 500 }}>{minutes} min</span>
      ),
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#52c41a" }}>
          {amount} MAD
        </span>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={statusColors[status]}>
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: "Méthode",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 120,
      render: (method) => (
        <Tag color={paymentMethodColors[method]}>
          {paymentMethodLabels[method] || method}
        </Tag>
      ),
    },
    {
      title: "Heure de début",
      dataIndex: "start_time",
      key: "start_time",
      width: 160,
      render: (date) => new Date(date).toLocaleString("fr-FR"),
    },
    {
      title: "Heure de fin",
      dataIndex: "end_time",
      key: "end_time",
      width: 160,
      render: (date) => new Date(date).toLocaleString("fr-FR"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => handleViewDetail(record)}
        >
          Détails
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <CarOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Tickets de Parking Citizen
            </Title>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={5}>
              <Select
                placeholder="Statut"
                style={{ width: "100%" }}
                value={filters.statusFilter}
                onChange={(value) => handleFilterChange("statusFilter", value)}
                allowClear
              >
                <Option value="pending">
                  <Tag color={statusColors.pending}>
                    {statusLabels.pending}
                  </Tag>
                </Option>
                <Option value="active">
                  <Tag color={statusColors.active}>{statusLabels.active}</Tag>
                </Option>
                <Option value="expired">
                  <Tag color={statusColors.expired}>
                    {statusLabels.expired}
                  </Tag>
                </Option>
                <Option value="cancelled">
                  <Tag color={statusColors.cancelled}>
                    {statusLabels.cancelled}
                  </Tag>
                </Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Input
                placeholder="Matricule"
                prefix={<SearchOutlined />}
                allowClear
                value={filters.matricule}
                onChange={(e) => handleFilterChange("matricule", e.target.value)}
                onPressEnter={handleSearch}
              />
            </Col>
            <Col xs={24} sm={12} md={7}>
              <RangePicker
                style={{ width: "100%" }}
                showTime
                format="YYYY-MM-DD HH:mm"
                placeholder={["Date début", "Date fin"]}
                onChange={handleDateRangeChange}
                value={
                  filters.startTimeFrom && filters.startTimeEnd
                    ? [
                        dayjs(filters.startTimeFrom),
                        dayjs(filters.startTimeEnd),
                      ]
                    : null
                }
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Sélectionner un utilisateur"
                style={{ width: "100%" }}
                value={filters.userId}
                onChange={(value) => handleFilterChange("userId", value)}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {users.map((user) => (
                  <Option key={user.user_id} value={user.user_id}>
                    {user.full_name ||
                      `${user.first_name || ""} ${
                        user.last_name || ""
                      }`.trim() ||
                      user.email ||
                      `User ${user.user_id}`}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  Rechercher
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  Réinitialiser
                </Button>
              </Space>
            </Col>
          </Row>
        </Space>

        <Table
          columns={columns}
          dataSource={tickets}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} tickets`,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>

      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>Détails du Ticket de Parking</span>
          </Space>
        }
        open={detailModalVisible}
        onCancel={handleCloseDetail}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseDetail}>
            Fermer
          </Button>,
        ]}
        width={800}
      >
        {selectedTicket && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="ID Ticket">
              <strong>{selectedTicket.id}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="UUID">
              <span style={{ fontFamily: "monospace", fontSize: 11 }}>
                {selectedTicket.uuid}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Plaque d'immatriculation">
              <Tag color="blue" icon={<CarOutlined />}>
                {selectedTicket.vehicle_plate}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="ID Véhicule">
              {selectedTicket.vehicle_id}
            </Descriptions.Item>
            <Descriptions.Item label="Nom du Parking">
              <strong>{selectedTicket.parking_name}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="ID Parking">
              {selectedTicket.parking_id}
            </Descriptions.Item>
            <Descriptions.Item label="Type de Parking">
              {selectedTicket.parking_type || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Nom de la Ligne">
              {selectedTicket.line_name}
            </Descriptions.Item>
            <Descriptions.Item label="ID Ligne">
              {selectedTicket.line_id}
            </Descriptions.Item>
            <Descriptions.Item label="Durée">
              <span style={{ fontWeight: 600, fontSize: 16 }}>
                {selectedTicket.duration_minutes} minutes
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Montant">
              <span
                style={{ fontWeight: 600, fontSize: 16, color: "#52c41a" }}
              >
                {selectedTicket.amount} MAD
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Méthode de paiement">
              <Tag color={paymentMethodColors[selectedTicket.payment_method]}>
                {paymentMethodLabels[selectedTicket.payment_method] ||
                  selectedTicket.payment_method}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Statut">
              <Tag color={statusColors[selectedTicket.status]}>
                {statusLabels[selectedTicket.status] || selectedTicket.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Heure de début">
              {new Date(selectedTicket.start_time).toLocaleString("fr-FR", {
                dateStyle: "full",
                timeStyle: "long",
              })}
            </Descriptions.Item>
            <Descriptions.Item label="Heure de fin">
              {new Date(selectedTicket.end_time).toLocaleString("fr-FR", {
                dateStyle: "full",
                timeStyle: "long",
              })}
            </Descriptions.Item>
            <Descriptions.Item label="Date de création">
              {new Date(selectedTicket.created_at).toLocaleString("fr-FR", {
                dateStyle: "full",
                timeStyle: "long",
              })}
            </Descriptions.Item>
            <Descriptions.Item label="Date de mise à jour">
              {new Date(selectedTicket.updated_at).toLocaleString("fr-FR", {
                dateStyle: "full",
                timeStyle: "long",
              })}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default CitizenParkingTicketsPage;
